import { z } from 'zod';
import { DateOnlySchema, IdSchema, ListQuerySchema, PageInfoSchema, TimestampSchema, VersionSchema, } from './common.js';
export const CallDirectionSchema = z.enum(['inbound', 'outbound']);
export const CallDispositionSchema = z.enum([
    'answered',
    'voicemail',
    'no_answer',
    'busy',
    'callback_requested',
    'other',
]);
/** Digits only with an optional leading plus; anything under seven digits is not a number. */
export function normalizePhoneNumber(value) {
    if (!value)
        return null;
    const plus = value.trim().startsWith('+') ? '+' : '';
    const digits = value.replace(/\D/g, '');
    return digits.length >= 7 ? `${plus}${digits}`.slice(0, 20) : null;
}
/** A manually logged call (specification 8.15). */
export const CallLogSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    direction: CallDirectionSchema,
    jobFileId: IdSchema.nullable(),
    contactId: IdSchema.nullable(),
    companyId: IdSchema.nullable(),
    participantName: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    startedAt: TimestampSchema,
    endedAt: TimestampSchema.nullable(),
    durationSeconds: z.number().int().nullable(),
    disposition: CallDispositionSchema,
    summary: z.string(),
    followUpTaskId: IdSchema.nullable(),
    loggedByMembershipId: IdSchema,
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const CallFollowUpSchema = z.strictObject({
    title: z.string().trim().min(1).max(300),
    dueDate: DateOnlySchema.nullable().default(null),
    owner: IdSchema.nullable().default(null),
});
const endsAfterStart = (value) => !value.startedAt || !value.endedAt || value.startedAt <= value.endedAt;
const ORDER = { path: ['endedAt'], message: 'The call cannot end before it starts' };
export const LogCallRequestSchema = z
    .strictObject({
    direction: CallDirectionSchema,
    jobFileId: IdSchema.nullable().default(null),
    contactId: IdSchema.nullable().default(null),
    companyId: IdSchema.nullable().default(null),
    participantName: z.string().trim().max(200).nullable().default(null),
    phoneNumber: z.string().trim().max(40).nullable().default(null),
    /** Defaults to now. */
    startedAt: TimestampSchema.optional(),
    endedAt: TimestampSchema.nullable().default(null),
    disposition: CallDispositionSchema,
    summary: z.string().trim().min(1).max(4000),
    /** Creates a task in the same transaction. */
    followUp: CallFollowUpSchema.nullable().default(null),
})
    .refine(endsAfterStart, ORDER)
    .refine((value) => value.contactId !== null || value.companyId !== null || value.participantName !== null, {
    path: ['participantName'],
    message: 'Say who was on the call: a contact, a company, or a name',
});
export const UpdateCallRequestSchema = z
    .strictObject({
    participantName: z.string().trim().max(200).nullable().optional(),
    phoneNumber: z.string().trim().max(40).nullable().optional(),
    startedAt: TimestampSchema.optional(),
    endedAt: TimestampSchema.nullable().optional(),
    disposition: CallDispositionSchema.optional(),
    summary: z.string().trim().min(1).max(4000).optional(),
    version: VersionSchema,
})
    .refine(endsAfterStart, ORDER);
export const CallListQuerySchema = ListQuerySchema.extend({
    jobFileId: IdSchema.optional(),
    contactId: IdSchema.optional(),
    companyId: IdSchema.optional(),
    direction: CallDirectionSchema.optional(),
    from: TimestampSchema.optional(),
    to: TimestampSchema.optional(),
});
export const CallListSchema = z.strictObject({
    items: z.array(CallLogSchema),
    page: PageInfoSchema,
});
